import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Music24PlanId } from '../../config/music24StripePlans';
import { SMLUpgradeFeature, getPlanCapabilities } from '../../config/music24PlanCapabilities';
import { planDescriptionMessages, planNameMessages } from '../../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import { UpgradeOffer } from './useUpgradeOffer';
import { featureRowLabel, featureRowValue, upgradeMessages } from './upgradeMessages';

/** Design b draws two plans in both frames. "Compare all plans" shows the rest. */
export const MAX_UPGRADE_PLAN_OPTIONS = 2;

export interface UpgradePlanOption {
  id: Music24PlanId;
  name: string;
  /** "$49 / month", or `null` inside a native app's web view (App Store 3.1.1). */
  price: string | null;
  /** The line under the name: the feature's own number, or the plan description. */
  detail: string;
  isSelected: boolean;
}

/**
 * The plan rows of design b: the plans that unlock the feature, cheapest first. When the feature
 * has a number that differs between the plans ("Scheduled auto-posts: 60 a month"), each row
 * quotes it. Otherwise the rows fall back to the plan description, so that they still differ.
 */
export function useUpgradePlanOptions(
  feature: SMLUpgradeFeature,
  offer: UpgradeOffer,
  options?: { hidePrice?: boolean }
): UpgradePlanOption[] {
  const hidePrice = options?.hidePrice ?? false;
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const { plans, plan: selectedPlan } = offer;

  return useMemo(() => {
    const shown = plans.slice(0, MAX_UPGRADE_PLAN_OPTIONS);
    const rowLabel = featureRowLabel(feature);
    const facts = shown.map((candidate) => featureRowValue(feature, getPlanCapabilities(candidate), intl));
    const factsDiffer = rowLabel !== undefined && new Set(facts).size === facts.length && facts.length > 1;

    return shown.map((candidate, index) => ({
      id: candidate.id,
      name: intl.formatMessage(planNameMessages[candidate.tier]),
      price: hidePrice
        ? null
        : intl.formatMessage(upgradeMessages.planPrice, {
            price: formatPlanPrice(intl, candidate.prices.monthly.amount, currency),
          }),
      detail:
        factsDiffer && rowLabel !== undefined
          ? intl.formatMessage(upgradeMessages.planFact, { label: intl.formatMessage(rowLabel), value: facts[index] })
          : intl.formatMessage(planDescriptionMessages[candidate.tier]),
      isSelected: candidate.id === selectedPlan.id,
    }));
  }, [intl, currency, feature, plans, selectedPlan, hidePrice]);
}
